import { errorMessage } from "./message"
import { MultiLanguageValue } from "@shared/types"
import { useHostStore } from "../stores/useHostStore"

export function useValidators() {
  const i18n = window.__I18N__
  const { t } = i18n
  return {
    email: (v: string) => {
      if (!v) return true
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return pattern.test(v) || t("general.validation.email")
    },
    required: (v: any) => {
      return !!v || t("general.validation.required")
    },
    minValue: (min: number) => (value: number) => {
      if (value === null || value === undefined) {
        return true
      }
      return value >= min || t("general.validation.minValue", { min })
    },
    maxValue: (max: number) => (value: number) => {
      if (value === null || value === undefined) {
        return true
      }
      return value <= max || t("general.validation.maxValue", { max })
    },
    onlyIntegers: (value: any) => {
      if (value === null || value === undefined || typeof value !== "number") {
        return true
      }
      return Number.isInteger(value) || t("general.validation.onlyIntegers")
    },
  }
}

/** Handlers **/
export const handleError = (error: any) => {
  if (typeof error === "string") {
    errorMessage(error)
  } else if (error.status === 403) {
    errorMessage("general.messages.forbidden")
  } else if (error.status === 404 && error.response && error.response.data instanceof Array) {
    error.response.data.map((errMsg: string) => errorMessage(errMsg))
  } else if (error.status === 400 && error.response && error.response.data.length > 0 && error.response.data instanceof Array) {
    error.response.data.map((errMsg: string) => errorMessage(errMsg))
  } else if (error.status === 400 || error.code == "ERR_BAD_RESPONSE" || error.code == "ERR_BAD_REQUEST") {
    errorMessage("general.messages.serverError")
  } else if (error.code === "ERR_NETWORK") {
    // The request was made but no response was received
    errorMessage("general.messages.networkError")
  } else if (error.error) {
    // Handle specific error structure
    errorMessage(error.error)
  } else {
    errorMessage("general.messages.serverError")
  }
}

export const formatFileSize = (size: number | undefined | null): string => {
  if (size === null || size === undefined) return "0 B"
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export const getInitials = (name: string): string => {
  if (!name) return ""
  const parts = name.split(" ")
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase()
}

export const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return ""
  const d = new Date(date)
  const i18n = window.__I18N__
  return d.toLocaleDateString(i18n.locale.value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
}

export const formatDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return ""
  const d = new Date(date)
  const i18n = window.__I18N__
  return d.toLocaleString(i18n.locale.value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const toCamelCase = (str: string): string => {
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
}

export const toCamelCaseFirstLetter = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toLowerCase() + str.slice(1)
}

export const getTranslatedLabel = (value: MultiLanguageValue | undefined | null): string => {
  const hostStore = useHostStore()
  const userStore = hostStore.getUserStore()
  const globalStore = hostStore.getGlobalStore()
  const i18n = window.__I18N__

  if (value == undefined || value == null || value.languages == null || value.languages.length === 0) {
    return ""
  }

  const currentLocale = userStore.user?.language || i18n.locale.value
  const languageEntry = value.languages.find((lang) => lang.languageCode === currentLocale)

  if (languageEntry && languageEntry.value) {
    return languageEntry.value
  }

  const baseLanguage = globalStore.configurations?.baseLanguage || "en-US"
  const baseLanguageEntry = value.languages.find((lang) => lang.languageCode === baseLanguage)
  if (baseLanguageEntry && baseLanguageEntry.value) {
    return baseLanguageEntry.value
  }

  return ""
}

export const getTranslatedList = (
  listToTranslate: any[] | undefined,
  fields: (string | { key: string; fields?: string[]; transform?: (value: any) => string })[],
) => {
  if (!listToTranslate || listToTranslate.length === 0) return []
  if (!fields || fields.length === 0) return []

  return listToTranslate.map((item) => {
    const translatedList: any = { ...item }
    fields.forEach((field) => {
      // Handle custom transformer object
      if (typeof field === "object" && field.key && field.transform) {
        const propName = `translated${String(field.key).charAt(0).toUpperCase() + String(field.key).slice(1)}`
        translatedList[propName] = field.transform(item[field.key])
        return
      }

      // Handle multi-field object
      if (typeof field === "object" && field.key && field.fields && Array.isArray(field.fields)) {
        field.fields.forEach((subField) => {
          const multiLangValue = item[field.key] ? (item[field.key][subField] as MultiLanguageValue) : null
          if (multiLangValue) {
            const propName = `translated${String(field.key).charAt(0).toUpperCase() + String(field.key).slice(1)}${
              String(subField).charAt(0).toUpperCase() + String(subField).slice(1)
            }`
            translatedList[propName] = getTranslatedLabel(multiLangValue)
          }
        })
        return
      }

      // Handle simple string field
      if (typeof field === "string") {
        if (field !== undefined && field !== null && (item[field] as MultiLanguageValue)) {
          const propName = `translated${String(field).charAt(0).toUpperCase() + String(field).slice(1)}`
          translatedList[propName] = getTranslatedLabel(item[field] as MultiLanguageValue)
        }
      }
    })
    return translatedList
  })
}

export const cleanArrayForComparison = (obj: any) => {
  const cleanObj = { ...obj }
  delete cleanObj.createdAt
  delete cleanObj.updatedAt
  delete cleanObj.createdBy
  delete cleanObj.updatedBy
  return cleanObj
}

export const checkUnsavedChanges = (object1: any, object2: any, disableCheckUnsavedChanges: boolean): boolean => {
  if (disableCheckUnsavedChanges) return false
  if (!object1 || !object2) return Object.keys(object1 || {}).length > 0

  const current = JSON.stringify(cleanArrayForComparison(object1))
  const original = JSON.stringify(cleanArrayForComparison(object2))
  return current !== original
}

export const downloadFileFromResponse = (response: any) => {
  // Create a Blob from the response data
  const blob = new Blob([response.data], { type: response.headers["content-type"] })

  // Create a URL for the Blob
  const url = window.URL.createObjectURL(blob)

  // Extract filename from content-disposition header
  const disposition = response.headers["content-disposition"] || ""
  const match = disposition.match(/filename="?([^;"]+)"?/)
  const fileName = match?.[1] || "File"

  // Create a temporary anchor element to trigger the download
  const link = document.createElement("a")
  link.href = url
  link.download = fileName

  // Append the link to the document, trigger the download, and remove the link
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Revoke the Blob URL to free up memory
  window.URL.revokeObjectURL(url)
}
